import {
  type PropType, computed, defineComponent, h, inject, reactive,
} from 'vue';
import {
  type RouteLocationRaw, type RouterLinkProps, routerKey,
} from 'vue-router';
import useQLink from '~/composables/useQLink';
import logger from '~/services/QLogger';
import { isNonProd, isServer } from '~/utils/EnvUtils';

const QLinkImpl = defineComponent({
  props: {
    to: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      required: true,
    },
    replace: Boolean,
    activeClass: String,
    // inactiveClass: String,
    exactActiveClass: String,
    custom: Boolean,
    ariaCurrentValue: {
      type: String as PropType<RouterLinkProps['ariaCurrentValue']>,
      default: 'page',
    },
    source: {
      type: String,
      default: '',
    },
  },

  useQLink,

  setup(props, { slots }) {
    const link = isServer() ? useQLink(props, false) : reactive(useQLink(props, false));

    const { options } = inject(routerKey)!;

    const elClass = computed(() => ({
      [getLinkClass(
        props.activeClass,
        options.linkActiveClass,
        'router-link-active',
      )]: link.isActive,
      [getLinkClass(
        props.exactActiveClass,
        options.linkExactActiveClass,
        'router-link-exact-active',
      )]: link.isExactActive,
    }));

    return () => {
      if (!props.source) {
        const msg = 'source prop was not provided in <q-link>';
        const err = new Error(`\nQRouter Exception :: ${msg}\n`);
        if (isNonProd()) {
          throw err;
        } else {
          logger.logOnSentry(['type: QRouter.exception', `message: ${msg}`], 'exception');
        }
      }
      const children = slots.default && slots.default(link);
      return props.custom
        ? children
        : h(
          'a',
          {
            'aria-current': link.isExactActive
              ? props.ariaCurrentValue
              : null,
            'href': link.href,
            // this would override user added attrs but Vue will still add
            // the listener, so we end up triggering both
            'onClick': link.navigate,
            'class': elClass.value,
          },
          children,
        );
    };
  },
});

/**
 * Utility class to get the active class based on defaults.
 * @param propClass
 * @param globalClass
 * @param defaultClass
 */
function getLinkClass(propClass: string | undefined,
  globalClass: string | undefined,
  defaultClass: string): string {
  return propClass != null
    ? propClass
    : globalClass != null
      ? globalClass
      : defaultClass;
}

export default QLinkImpl;
